import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, PauseCircle, RefreshCw, Loader2 } from "lucide-react";
import api from "@/lib/api";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm py-1 border-b border-slate-100 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium text-right break-all">{value ?? "—"}</span>
  </div>
);

function ProviderCard({ name, testId, state, rows }) {
  const { ok, disabled, error } = state || {};
  const Icon = ok ? CheckCircle2 : disabled ? PauseCircle : XCircle;
  const tone = ok ? "text-emerald-600" : disabled ? "text-slate-400" : "text-rose-600";
  const badge = ok
    ? { text: "Working", cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" }
    : disabled
      ? { text: "Switched off", cls: "bg-slate-100 text-slate-600 hover:bg-slate-100" }
      : { text: "Not working", cls: "bg-rose-100 text-rose-700 hover:bg-rose-100" };

  return (
    <Card className="p-6 border-slate-200" data-testid={testId}>
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 shrink-0 ${tone}`} />
        <h3 className="font-display text-lg font-bold text-slate-900 flex-1">{name}</h3>
        <Badge className={badge.cls} data-testid={`${testId}-badge`}>{badge.text}</Badge>
      </div>
      {error && (
        <p className={`mt-3 text-sm ${disabled ? "text-slate-500" : "text-rose-700"}`} data-testid={`${testId}-error`}>{error}</p>
      )}
      <div className="mt-4">{rows.map((r) => <Row key={r.label} {...r} />)}</div>
    </Card>
  );
}

export default function PaymentHealthPanel() {
  const [stripe, setStripe] = useState(null);
  const [paypal, setPaypal] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    const [s, p] = await Promise.all([
      api.get("/admin/stripe/health").then((r) => r.data).catch((e) => ({ ok: false, error: e?.response?.data?.detail || "Could not reach the server" })),
      api.get("/admin/paypal/health").then((r) => r.data).catch((e) => ({ ok: false, error: e?.response?.data?.detail || "Could not reach the server" })),
    ]);
    setStripe(s);
    setPaypal(p);
    setBusy(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (busy && !stripe) return <div className="py-8"><Loader2 className="w-6 h-6 animate-spin text-teal-600" data-testid="payment-health-loading" /></div>;

  return (
    <div className="space-y-4" data-testid="payment-health-panel">
      <div className="grid md:grid-cols-2 gap-4">
        <ProviderCard
          name="Stripe (card payments)"
          testId="health-stripe"
          state={stripe}
          rows={[
            { label: "Mode", value: stripe?.live_mode === undefined ? (stripe?.key_prefix?.includes("live") ? "Live" : stripe?.key_prefix ? "Test" : "—") : (stripe.live_mode ? "Live" : "Test") },
            { label: "Secret key", value: stripe?.key_present ? `${stripe.key_prefix}… (${stripe.key_length} chars)` : "Missing" },
            { label: "Stripe account", value: stripe?.account_id },
            { label: "Can accept real charges", value: stripe?.charges_enabled === undefined ? "—" : stripe.charges_enabled ? "Yes" : stripe.live_mode ? "No" : "Test mode — no real charges" },
          ]}
        />
        <ProviderCard
          name="PayPal"
          testId="health-paypal"
          state={paypal}
          rows={[
            { label: "Shown at checkout", value: paypal?.ok ? "Yes" : "No" },
            { label: "Mode", value: paypal?.mode ? paypal.mode.charAt(0).toUpperCase() + paypal.mode.slice(1) : "—" },
            { label: "Client ID", value: paypal?.client_id_present ? `${paypal.client_id_prefix}… (${paypal.client_id_length} chars)` : "Missing" },
            { label: "Secret", value: paypal?.secret_present ? "Present" : "Missing" },
          ]}
        />
      </div>
      <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-full" data-testid="payment-health-refresh">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-check now
      </Button>
      <p className="text-xs text-slate-500">
        Checked live against Stripe and PayPal each time you load this page. Keys are never shown in full.
      </p>
    </div>
  );
}
